import React, { useState, useCallback } from 'react';
import { cn } from '@/lib/utils';
import { MarketingTarotCard } from './MarketingTarotCard';
import { MARKETING_TAROT_DECK, getRandomCard } from '@/data/marketingTarot';

export const CardDeck = ({ 
  onCardSelect, 
  isShuffling = false,
  className 
}) => {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [deck] = useState(MARKETING_TAROT_DECK);
  
  // Show all 10 cards in the fan spread
  const visibleCards = deck;
  const centerIndex = Math.floor(visibleCards.length / 2);

  const getCardStyle = (index) => {
    const offset = index - centerIndex;
    const rotation = offset * 5; // 5 degrees between each card
    const translateY = Math.abs(offset) * 6; // Creates the arc
    const translateX = offset * 35; // Spread cards horizontally
    const zIndex = visibleCards.length - Math.abs(offset);
    
    const isHovered = hoveredIndex === index;
    const hoverLift = isHovered ? -35 : 0;
    const hoverScale = isHovered ? 1.12 : 1;
    
    return {
      transform: `
        translateX(${translateX}px) 
        translateY(${translateY + hoverLift}px) 
        rotate(${rotation}deg)
        scale(${hoverScale})
      `,
      zIndex: isHovered ? 100 : zIndex,
      transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
    };
  };

  const handleCardClick = useCallback((index) => {
    const randomCard = getRandomCard();
    onCardSelect?.(randomCard);
  }, [onCardSelect]);

  return (
    <div className={cn("relative", className)} data-testid="card-deck">
      {/* Deck base glow */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="w-96 h-96 bg-[#e89b47]/10 rounded-full blur-3xl animate-pulse"/>
      </div>

      {/* Fan of cards */}
      <div 
        className={cn(
          "relative flex items-end justify-center h-80",
          isShuffling && "animate-shuffle"
        )}
        style={{ perspective: '1200px' }}
      >
        {visibleCards.map((card, index) => (
          <div
            key={card.id}
            className="absolute cursor-pointer"
            style={{
              ...getCardStyle(index),
              transformOrigin: 'bottom center',
            }}
            onMouseEnter={() => setHoveredIndex(index)}
            onMouseLeave={() => setHoveredIndex(null)}
            onClick={() => handleCardClick(index)}
          >
            <MarketingTarotCard
              card={card}
              isFlipped={false}
              size="default"
              isHoverable={false}
            />
          </div>
        ))}
      </div>

      {/* Deck info */}
      <div className="text-center mt-6">
        <p className="text-[#d4c4a8]/60 font-body text-sm">
          {deck.length} cards in deck
        </p>
        <p className="text-[#d4c4a8]/40 font-body text-xs mt-1">
          Hover over cards • Click to draw
        </p>
      </div>
    </div>
  );
};

export default CardDeck;
